/**
 * Map Utility Functions
 * 
 * Helper functions for map calculations and operations
 */

import { TerrainMap, AxialCoord, TerrainType } from '../types/terrain';
import { hexToPixel, hexCorners } from './hexMath';

/**
 * Calculate the zoom level and center needed to fit the entire island (all land hexes) on screen
 * @param map The terrain map
 * @param canvasWidth Canvas width in pixels
 * @param canvasHeight Canvas height in pixels
 * @param hexSize Base hex size
 * @param padding Padding percentage (0-1) to leave around the island
 * @returns Object with zoom level and center coordinates
 */
export function calculateFitToScreenZoom(
  map: TerrainMap,
  canvasWidth: number,
  canvasHeight: number,
  hexSize: number = 30,
  padding: number = 0.1
): { zoom: number; center: AxialCoord } {
  // Find all land hexes (non-ocean)
  const landHexes: AxialCoord[] = [];
  for (const hex of Array.from(map.hexes.values())) {
    if (hex.terrain !== TerrainType.Ocean) {
      landHexes.push({ q: hex.q, r: hex.r });
    }
  }

  if (landHexes.length === 0) {
    return { zoom: 1, center: { q: 0, r: 0 } }; // Default if no land hexes
  }

  // Calculate bounding box of all land hexes
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const coord of landHexes) {
    const corners = hexCorners(coord, hexSize);
    for (const corner of corners) {
      minX = Math.min(minX, corner.x);
      maxX = Math.max(maxX, corner.x);
      minY = Math.min(minY, corner.y);
      maxY = Math.max(maxY, corner.y);
    }
  }

  const boundingWidth = maxX - minX;
  const boundingHeight = maxY - minY;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  // Calculate zoom needed to fit with minimal padding (zoom in more)
  // Use less padding to show more of the island and less ocean
  const availableWidth = canvasWidth * (1 - padding * 2);
  const availableHeight = canvasHeight * (1 - padding * 2);

  const zoomX = availableWidth / boundingWidth;
  const zoomY = availableHeight / boundingHeight;

  // Use the smaller zoom to ensure everything fits
  let zoom = Math.min(zoomX, zoomY);
  
  // Zoom in more aggressively - multiply by a factor to show less ocean
  // Factor scales with screen size: larger screens get more zoom
  const screenSizeFactor = Math.min(canvasWidth, canvasHeight) / 600; // Normalize to 600px baseline
  const zoomMultiplier = 1.0 + (screenSizeFactor * 0.3); // 1.0 to 1.3x multiplier based on screen size
  zoom = zoom * zoomMultiplier;

  // Clamp to reasonable bounds (don't zoom out too far, but allow zooming in)
  const clampedZoom = Math.max(0.1, Math.min(2, zoom));

  // Find the hex closest to the center of the bounding box
  // Convert pixel center back to hex coordinates
  const centerHex = findClosestHexToPixel(
    { x: centerX, y: centerY },
    landHexes,
    hexSize
  );

  return { zoom: clampedZoom, center: centerHex };
}

/**
 * Find the hex closest to a given pixel coordinate
 */
function findClosestHexToPixel(
  pixel: { x: number; y: number },
  hexes: AxialCoord[],
  hexSize: number
): AxialCoord {
  let closestHex = hexes[0];
  let minDistance = Infinity;

  for (const hex of hexes) {
    const hexPixel = hexToPixel(hex, hexSize);
    const dx = hexPixel.x - pixel.x;
    const dy = hexPixel.y - pixel.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < minDistance) {
      minDistance = distance;
      closestHex = hex;
    }
  }

  return closestHex;
}

